import React, { forwardRef, useEffect, useRef, useState } from 'react'

import setaDropdownVerde from '../../../icones/icone-seta-dropdown-verde.png'
import Checkbox from '../../Mantine/Checkbox'

import * as S from './styles'
import { GlobalStyles } from './styles'

// padding lateral do item do dropdown + checkbox + gap
const LARGURA_ELEMENTOS_INTERNOS = 20

const MAX_SEARCH_LENGTH = 20

const VALOR_TODOS = 'ALL'

/**
 * @typedef {Object} SelectOption
 * @property {string} value
 * @property {string} label
 */

/**
 * @typedef {Object} MultiSelectProps
 * @property {React.ReactNode} [label]
 * @property {SelectOption[]} [options]
 * @property {(value: string[]) => void} [onChange]
 * @property {string | number} [width]
 * @property {string | number} [w]
 * @property {string[]} [value]
 * @property {boolean} [disabled]
 * @property {boolean} [searchable]
 * @property {number} [maxSelectedValues]
 * @property {boolean} [withSelectAll] Adiciona a opção "Todos" no início do dropdown
 */

/**
 * Componente de seleção múltipla customizado com suporte a busca, limite de seleção,
 * e visual com checkbox.
 *
 * @param {MultiSelectProps} props
 */
export default function MultiSelect({
  label = '',
  options = [],
  onChange,
  width,
  w,
  value,
  disabled,
  searchable = false,
  maxSelectedValues,
  withSelectAll = false
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [larguraSelect, setLarguraSelect] = useState(0)

  const refSelect = useRef()
  const refWrapper = useRef()

  useEffect(() => {
    if (!refWrapper.current) return undefined
    const atualizarLargura = () => {
      const novaLargura = refWrapper.current?.getBoundingClientRect()?.width || 0
      setLarguraSelect(novaLargura)
    }
    atualizarLargura()
    const observer = new ResizeObserver(atualizarLargura)
    observer.observe(refWrapper.current)
    return () => observer.disconnect()
  }, [])

  const larguraTextoItem =
    larguraSelect > 0
      ? Math.max(larguraSelect - LARGURA_ELEMENTOS_INTERNOS, 0)
      : undefined

  // eslint-disable-next-line react/no-unstable-nested-components
  const CustomItemComponent = forwardRef((props, ref) => (
    <CustomItem ref={ref} width={larguraTextoItem} {...props} />
  ))
  CustomItemComponent.displayName = 'CustomItemComponent'

  const filterData = (query, selected, data) => {
    if (!query) return data
    return data.label.toLowerCase().includes(query.toLowerCase())
  }

  const itensSelecionados = (value || []).filter((v) => v !== VALOR_TODOS)
  const hasTodosSelecionados =
    withSelectAll &&
    options.length > 0 &&
    itensSelecionados.length === options.length

  const opcoes = withSelectAll
    ? [{ value: VALOR_TODOS, label: 'Todos' }, ...options]
    : options

  const valoresSelecionados = withSelectAll
    ? hasTodosSelecionados
      ? [VALOR_TODOS, ...itensSelecionados]
      : itensSelecionados
    : value

  const handleInternalChange = (novosValores) => {
    if (!withSelectAll) {
      onChange?.(novosValores)
      return
    }
    const haviaTodos = hasTodosSelecionados
    const incluiTodos = novosValores.includes(VALOR_TODOS)

    if (!haviaTodos && incluiTodos) {
      onChange?.(options.map((o) => o.value))
      return
    }
    if (haviaTodos && !incluiTodos) {
      onChange?.([])
      return
    }
    onChange?.(novosValores.filter((v) => v !== VALOR_TODOS))
  }

  const handleCloseDropdown = () => {
    setIsOpen(false)
    setSearch('')
  }

  const onSearchChange = (val) => {
    if (val.length <= MAX_SEARCH_LENGTH) setSearch(val)
  }

  const handleOpenDropdown = (e) => {
    e.stopPropagation()
    const wrapper = refSelect.current?.closest('.mantine-MultiSelect-wrapper')
    const input = wrapper?.querySelector('input')
    if (input) {
      input.focus()
      input.dispatchEvent(
        new MouseEvent('mousedown', { bubbles: true, cancelable: true })
      )
    }
  }

  return (
    <div ref={refWrapper} style={{ width: '100%' }}>
      <GlobalStyles />
      <S.MultiSelect
        ref={refSelect}
        searchValue={search}
        onSearchChange={onSearchChange}
        searchable={searchable}
        width={width}
        w={w}
        onChange={handleInternalChange}
        disabled={disabled}
        radius="xl"
        zIndex={9999}
        nothingFound="Nenhum item encontrado"
        withinPortal
        disableSelectedItemFiltering
        maxSelectedValues={maxSelectedValues}
        value={valoresSelecionados}
        filter={filterData}
        placeholder="Selecione"
        data={opcoes}
        label={label}
        onDropdownOpen={() => setIsOpen(true)}
        onDropdownClose={handleCloseDropdown}
        rightSection={
          <S.Img
            isOpen={isOpen}
            alt="seta-dropdown"
            src={setaDropdownVerde}
            onClick={handleOpenDropdown}
          />
        }
        itemComponent={CustomItemComponent}
        valueComponent={(props) => (
          <Value
            key={`${label}-${props.index}`}
            allSelected={value}
            options={options || []}
            isOpen={isOpen}
            searchable={searchable}
            {...props}
          />
        )}
      />
    </div>
  )
}

const Value = ({ allSelected, isOpen, searchable, ...others }) => {
  if (others.index > 0 || (searchable && isOpen)) return null

  const totalSelecionados = (allSelected || []).filter(
    (v) => v !== VALOR_TODOS
  ).length
  const moreThanOne = totalSelecionados > 1

  const formattedLabel = (
    <S.Label key="more">
      {moreThanOne ? `${totalSelecionados} Selecionados` : '1 Selecionado'}
    </S.Label>
  )

  return <S.LabelWrapper {...others}>{formattedLabel}</S.LabelWrapper>
}

const CustomItem = forwardRef(({ label, width, ...props }, ref) => {
  return (
    <S.CustomItemWrapper ref={ref} {...props}>
      <Checkbox checked={props.selected} onChange={() => { }} />
      <div style={{ width }}>
        <S.CustomItemSpan>{label}</S.CustomItemSpan>
      </div>
    </S.CustomItemWrapper>
  )
})

CustomItem.displayName = 'CustomItem'
